import { useQuery } from '@tanstack/react-query';
import { MetricasApi, ColaboradoresApi, UsuariosApi, FechamentoApi } from '../api/client.js';
import { Table } from '../components/Table.jsx';
import { useState, useMemo, useEffect } from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip as RTTooltip, BarChart, Bar, PieChart, Pie, Cell, Legend } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../utils/formatData.js';
import { useAuth } from '../context/AuthContext.jsx';

export default function DashboardPage() {
  const hoje = new Date();
  const ano = hoje.getFullYear();
  const mes = hoje.getMonth()+1;
  const { perfil, auth } = useAuth();
  const perfilNormalizado = String(perfil || '').trim().toUpperCase();
  const isAuthReady = Boolean(perfilNormalizado);
  const isColaborador = perfilNormalizado === 'COLABORADOR';
  const isLider = perfilNormalizado === 'LIDER';
  const colaboradorIdStorage = localStorage.getItem('defaultColaboradorId') || '';
  const [limite,setLimite] = useState(10);
  const [colaboradorFiltro, setColaboradorFiltro] = useState(
    isColaborador ? colaboradorIdStorage : '',
  );
  const navigate = useNavigate();

  useEffect(() => {
    if (!isColaborador) return;
    if (!colaboradorIdStorage) return;
    if (colaboradorFiltro === colaboradorIdStorage) return;
    setColaboradorFiltro(colaboradorIdStorage);
  }, [isColaborador, colaboradorIdStorage, colaboradorFiltro]);

  // Calcula último dia válido do mês (evita datas inválidas como 2025-09-31 que resultam em zero registros)
  const ultimoDia = new Date(ano, mes, 0).getDate();
  const inicioPeriodo = `${ano}-${String(mes).padStart(2,'0')}-01`;
  const fimPeriodo = `${ano}-${String(mes).padStart(2,'0')}-${String(ultimoDia).padStart(2,'0')}`;
  const colaboradorFiltroEfetivo = isColaborador
    ? colaboradorIdStorage
    : colaboradorFiltro;
  const { data: respMetricas } = useQuery({
    enabled: isAuthReady && (!isColaborador || !!colaboradorFiltroEfetivo),
    queryKey:[
      'metricas',
      'dashboard',
      colaboradorFiltroEfetivo,
      limite,
      inicioPeriodo,
      fimPeriodo,
    ],
    queryFn: () => MetricasApi.listar({
      pageSize: limite,
      page:1,
      colaboradorId: colaboradorFiltroEfetivo || undefined,
      inicio: inicioPeriodo,
      fim: fimPeriodo
    })
  });
  const metricas = Array.isArray(respMetricas) ? respMetricas : (respMetricas?.data || []);
  const { data: respColabs } = useQuery({
    enabled: isAuthReady && !isColaborador,
    queryKey:['colaboradores','dashboard'],
    queryFn: () => ColaboradoresApi.listar()
  });
  const colaboradores = Array.isArray(respColabs) ? respColabs : (respColabs?.data || []);
  const { data: respUsers } = useQuery({
    enabled: isAuthReady && !isColaborador && !isLider,
    queryKey:['usuarios','dashboard'],
    queryFn: () => UsuariosApi.listar({ pageSize:50 })
  });
  const usuarios = Array.isArray(respUsers) ? respUsers : (respUsers?.data || []);
  const { data: statusFech } = useQuery({
    enabled: isAuthReady && !isColaborador,
    queryKey:['fechamento','status', ano, mes],
    queryFn: () => FechamentoApi.status(ano, mes)
  });

  const totalRegistros = metricas.length;
  const somaPontos = metricas.reduce((acc,m)=> acc + (Number(m.total_pontos)||0), 0);
  const somaValor = metricas.reduce((acc,m)=> acc + (Number(m.valor_diario)||0), 0);

  // Mapa ID -> Nome amigável
  const nomePorColab = useMemo(()=> {
    const map = new Map();
    colaboradores.forEach(c => {
      map.set(c.id, c.usuario?.nome || c.usuario?.email || c.id);
    });
    return map;
  }, [colaboradores]);

  const ranking = useMemo(()=>{
    const map = new Map();
    metricas.forEach(m => {
      const key = m.colaborador_id || m.colaboradorId || 'desconhecido';
      const item = map.get(key) || { colaborador:key, pontos:0, valor:0 };
      item.pontos += Number(m.total_pontos)||0; item.valor += Number(m.valor_diario)||0; map.set(key,item);
    });
    return Array.from(map.values()).sort((a,b)=> b.pontos - a.pontos).slice(0,5).map(r => ({
      ...r,
      colaboradorNome: nomePorColab.get(r.colaborador) || r.colaborador
    }));
  },[metricas, nomePorColab]);

  const sparkData = useMemo(()=> metricas.slice(-14).map(m=> ({ d: m.data.slice(-2), t: m.total_pontos })), [metricas]);
  const mediaPontosDia = useMemo(()=> { if (!metricas.length) return 0; const diasUnicos = new Set(metricas.map(m=>m.data)); return (somaPontos / diasUnicos.size) || 0; }, [metricas, somaPontos]);
  const mediaValorDia = useMemo(()=> { if (!metricas.length) return 0; const diasUnicos = new Set(metricas.map(m=>m.data)); return (somaValor / diasUnicos.size) || 0; }, [metricas, somaValor]);

  // Dados agregados para gráficos
  const pontosPorColab = useMemo(()=>{
    const map = new Map();
    metricas.forEach(m=>{ const k = m.colaborador_id || m.colaboradorId || 'desconhecido'; map.set(k, (map.get(k)||0) + (Number(m.total_pontos)||0)); });
    return Array.from(map.entries()).slice(0,5).map(([k,v])=> ({ colaborador:k, nome:(nomePorColab.get(k)||k), pontos:v }));
  },[metricas, nomePorColab]);
  const valorPorDia = useMemo(()=>{
    const map = new Map();
    metricas.forEach(m=>{ map.set(m.data, (map.get(m.data)||0)+(Number(m.valor_diario)||0)); });
    return Array.from(map.entries()).sort(([a],[b])=> a.localeCompare(b)).slice(-10).map(([d,v])=> ({ data:d.slice(-2), valor:Number(v.toFixed(2)) }));
  },[metricas]);
  const fechamentoPieData = useMemo(()=>{
    if(!statusFech) return [];
    const pend = statusFech.pendentes || 0; const proc = (statusFech.total||0) - pend; return [
      { name:'Processados', value: proc },
      { name:'Pendentes', value: pend }
    ];
  },[statusFech]);
  const pieColors = ['#0B8457', '#c8102e'];

  return (
    <div>
      <h2 className="page-title">Dashboard</h2>
      <div className="card" style={{marginBottom:'1rem'}}>
        <div className="flex" style={{justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:'1rem'}}>
          <div style={{flex:'1 1 260px'}}>
            <strong>Resumo Rápido</strong>
            <p className="muted" style={{fontSize:'.65rem'}}>Mês: {String(mes).padStart(2,'0')}/{ano}. Filtro global opcional por colaborador.</p>
            <div style={{marginTop:'.4rem'}}>
              <label style={{fontSize:'.6rem', fontWeight:600}}>Colaborador</label>
              <select
                value={colaboradorFiltroEfetivo}
                onChange={e=>setColaboradorFiltro(e.target.value)}
                disabled={isColaborador}
                style={{display:'block', fontSize:'.7rem', marginTop:4, minWidth:'200px'}}
              >
                <option value="">(Todos)</option>
                {isColaborador && colaboradorFiltroEfetivo && (
                  <option value={colaboradorFiltroEfetivo}>
                    {auth?.usuario?.nome || auth?.usuario?.email || colaboradorFiltroEfetivo}
                  </option>
                )}
                {colaboradores.map(c=> <option key={c.id} value={c.id}>{c.usuario?.nome || c.usuario?.email || c.id}</option>)}
              </select>
            </div>
          </div>
          <div style={{flex:'2 1 300px', minWidth:'240px'}}>
            <div style={{fontSize:'.55rem', fontWeight:600, textTransform:'uppercase', marginBottom:4}}>Sparkline Pontos (últimos {sparkData.length} dias)</div>
            <div style={{height:80}}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={sparkData} margin={{left:5,right:5,top:5,bottom:5}}>
                  <XAxis hide dataKey="d" />
                  <YAxis hide domain={[0,4]} />
                  <RTTooltip labelFormatter={l=>`Dia ${l}`} formatter={(v)=>[v,'Pontos']} />
                  <Line type="monotone" dataKey="t" stroke="var(--color-blue)" strokeWidth={2} dot={false} activeDot={{r:3}} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="flex gap-md" style={{flex:'3 1 400px', flexWrap:'wrap'}}>
            <Stat label="Registros (amostra)" value={totalRegistros} color="var(--color-blue)" />
            <Stat label="Pontos" value={somaPontos} color="var(--color-orange)" />
            <Stat label="Valor (R$)" value={somaValor.toFixed(2)} color="var(--color-green)" />
            <Stat label="Média Pts/Dia" value={mediaPontosDia.toFixed(2)} color="#6F5AA8" />
            <Stat label="Média Valor/Dia" value={mediaValorDia.toFixed(2)} color="#0B787E" />
            <Stat label="Fechamento" value={statusFech? (statusFech.consolidado? 'OK':'Pendente') : '...'} color={statusFech?.consolidado? 'var(--color-success)':'var(--color-warning)'} />
          </div>
        </div>
      </div>

      <div className="flex gap-md" style={{flexWrap:'wrap'}}>
        <div className="card" style={{flex:'1 1 340px', minWidth:'300px'}}>
          <div className="flex" style={{justifyContent:'space-between', alignItems:'center'}}>
            <h3 style={{margin:0, fontSize:'.8rem'}}>Últimas Métricas</h3>
            <div className="flex" style={{gap:'.4rem', alignItems:'center'}}>
              <select value={limite} onChange={e=>setLimite(Number(e.target.value))} style={{fontSize:'.6rem'}}>
                {[10,20,50].map(n=> <option key={n} value={n}>Exibir {n}</option>)}
              </select>
              <button className="btn outline" style={{fontSize:'.55rem', padding:'.25rem .45rem'}} onClick={()=>navigate('/metricas')}>Ver todos</button>
            </div>
          </div>
          <div className="chart-center" style={{marginTop:8}}>
            {(() => { const w = Math.min(Math.max(valorPorDia.length * 55, 240), 520); return (
              <BarChart width={w} height={140} data={valorPorDia} margin={{top:5,right:10,left:0,bottom:0}}>
                <XAxis dataKey="data" fontSize={10} />
                <YAxis fontSize={10} />
                <RTTooltip formatter={(v)=> [v,'Valor']} labelFormatter={l=>`Dia ${l}`} />
                <Bar dataKey="valor" fill="var(--color-blue)" radius={[4,4,0,0]} />
              </BarChart>
            ); })()}
          </div>
          <Table
            keyField="id"
            columns={[
              { key:'data', header:'Data', thStyle:{whiteSpace:'nowrap'}, render: v => formatDate(v) },
              { key:'total_pontos', header:'Pts' },
              { key:'valor_diario', header:'R$', render:v=> Number(v).toFixed(2) },
              { key:'colaborador_id', header:'Colab', render:(v)=> {
                const nome = nomePorColab.get(v) || v || '';
                return nome.length>16? nome.slice(0,16)+'…': nome;
              } }
            ]}
            data={metricas}
          />
        </div>
        <div className="card" style={{flex:'1 1 300px', minWidth:'260px'}}>
          <h3 style={{marginTop:0, fontSize:'.8rem'}}>Ranking (Amostra)</h3>
          <div className="flex" style={{justifyContent:'flex-end', marginBottom:4}}>
            <button className="btn outline" style={{fontSize:'.55rem', padding:'.25rem .45rem'}} onClick={()=>navigate('/relatorios')}>Relatórios</button>
          </div>
          <div className="chart-center">
            {(() => { const w = Math.min(Math.max(pontosPorColab.length * 70, 240), 520); return (
              <BarChart width={w} height={140} data={pontosPorColab} margin={{top:5,right:10,left:0,bottom:0}}>
                <XAxis dataKey="nome" fontSize={10} interval={0} tickFormatter={(v)=> v.length>8? v.slice(0,8)+'…':v} />
                <YAxis fontSize={10} />
                <RTTooltip />
                <Bar dataKey="pontos" fill="var(--color-orange)" radius={[4,4,0,0]} />
              </BarChart>
            ); })()}
          </div>
          <Table
            keyField="colaborador"
            columns={[
              { key:'colaboradorNome', header:'Colaborador', render:(v)=> v.length>18? v.slice(0,18)+'…': v },
              { key:'pontos', header:'Pontos' },
              { key:'valor', header:'Valor (R$)', render:v=> v.toFixed(2) }
            ]}
            data={ranking}
          />
        </div>
        {statusFech && <div className="card" style={{flex:'1 1 260px', minWidth:'240px'}}>
          <h3 style={{marginTop:0, fontSize:'.8rem'}}>Fechamento</h3>
          <div className="flex" style={{justifyContent:'flex-end', marginBottom:4}}>
            <button className="btn outline" style={{fontSize:'.55rem', padding:'.25rem .45rem'}} onClick={()=>navigate('/fechamento')}>Detalhes</button>
          </div>
          <div style={{height:140}}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={fechamentoPieData} dataKey="value" nameKey="name" innerRadius={30} outerRadius={55} paddingAngle={2}>
                  {fechamentoPieData.map((d,i)=>(<Cell key={d.name} fill={pieColors[i % pieColors.length]} />))}
                </Pie>
                <Legend verticalAlign="bottom" height={20} wrapperStyle={{fontSize:10}} />
                <RTTooltip formatter={(v,n)=> [v,n]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <Table
            keyField="k"
            columns={[{ key:'k', header:'Indicador' }, { key:'v', header:'Valor' }]}
            data={[
              { k:'Total Registros', v: statusFech.total },
              { k:'Pendentes', v: statusFech.pendentes },
              { k:'Consolidado', v: statusFech.consolidado? 'Sim':'Não' }
            ]}
          />
        </div>}
      </div>
    </div>
  );
}

function Stat({label,value,color}) { return <div className="card" style={{minWidth:'140px', flex:'1 1 140px', borderColor:color}}><div style={{fontSize:'.65rem', textTransform:'uppercase', fontWeight:600, letterSpacing:'.5px', color:'var(--color-text-soft)'}}>{label}</div><div style={{fontSize:'1.4rem', fontWeight:700, color}}>{value}</div></div>; }
